import React, { useRef, useState } from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertExecution from '@app/model/alert/AlertExecution';
import { useQuery } from '@tanstack/react-query';
import AlertService from '@app/model/alert/service/AlertService';
import { AlertExecutionQueryKeys } from '@app/model/alert/service/AlertQueryKeys';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import Utilities from '@util/Utilities';
import PriceComparisonAlertRulePricePoint from '@app/model/alert/PriceComparisonAlertRulePricePoint';
import Tag from '@app/component/tag/Tag';
import _isEmpty from 'lodash/isEmpty';
import PriceComparisonAlertExecutionMostImportantNotificationsViewFragment
	from '@app/site/secured/alert-execution/fragment/price-comparison/PriceComparisonAlertExecutionMostImportantNotificationsViewFragment';
import PriceComparisonAlertExecutionAllNotificationsViewFragment
	from '@app/site/secured/alert-execution/fragment/price-comparison/PriceComparisonAlertExecutionAllNotificationsViewFragment';
import Message from '@app/component/message/Message';
import MarketStateDetailsModal from '@app/site/secured/alert/modal/MarketStateDetailsModal';
import PriceComparisonAlertExecutionUpdatedNotificationsFragment
	from '@app/site/secured/alert-execution/fragment/price-comparison/PriceComparisonAlertExecutionUpdatedNotificationsFragment';
import { useCompanyHotelSettings } from '@app/site/secured/provider/useCompanyHotelSettings';

const PriceComparisonAlertExecutionFragment: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	alertExecution: AlertExecution,
	isInDrawer?: boolean
}> = ({ companyHotel, alertExecution, isInDrawer = false }) => {
	const tabs = [
		{ id: 'MOST_IMPORTANT', name: 'Most Important Alerts' },
		{ id: 'ALL', name: 'All Alerts' },
	];
	const [currentTabId, setCurrentTabId] = useState<string>(tabs[0].id);

	const marketStateDetailsModal = useRef<any>(undefined);
	const { settings } = useCompanyHotelSettings();

	const { data: notificationOutputs, isLoading, isSuccess } = useQuery({
		queryFn: () => AlertService.findPriceComparisonAlertExecutionNotifications(alertExecution.id),
		queryKey: AlertExecutionQueryKeys.notifications(alertExecution.id),
	});

	if (isLoading) {
		return <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
			{[...Array(3)].map((x, i) =>
				<div key={i} className="bg-white rounded-xl mt-8 p-4 sm:p-8 relative">
					<div className="flex flex-col justify-between h-full">
						<div className="px-3 py-2">
							<time dateTime="2024-01-11"></time>
						</div>
						<div className="px-3 py-2">
							<div className="w-full animate-pulse">
								<div className="h-3.5 bg-gray-200 rounded-full w-8 mb-2.5"></div>
								<div className="h-2.5 bg-gray-200 rounded-full mb-2"></div>
								<div className="h-2.5 bg-gray-200 rounded-full mb-2"></div>
								<div className="h-2.5 bg-gray-200 rounded-full mb-2"></div>
							</div>
						</div>
					</div>
				</div>,
			)}
		</div>;
	}

	if (isSuccess) {

		const updatedNotificationOutputs = notificationOutputs.filter(o => o.isNew || o.isUpdated);
		const areAllNew = updatedNotificationOutputs.length === notificationOutputs.length;

		return <>
			<header className="bg-white shadow-sm">
				<div className="mx-auto max-w-7xl py-4 px-4 sm:px-6 lg:px-8">
					<div className="md:flex md:items-center md:justify-between">
						{!isInDrawer && <Link to="/secured/alerts" className="btn btn-white rounded-full p-0.5 mr-3"
																	title="Go back">
							<ChevronLeftIcon className="h-4 w-4" />
						</Link>}
						{!!alertExecution && <div
							className={Utilities.classNames('flex-1 md:flex md:items-center', isInDrawer ? 'md:justify-end' : 'md:justify-between')}>
							{!isInDrawer && <h1
								className="text-lg font-semibold leading-6 text-gray-900">{alertExecution.alertOutput.alert.name} details</h1>}
							<span
								className="text-xs text-gray-400">Executed at {Utilities.formatDateString(alertExecution.executionDate, 'DD/MM/YYYY HH:mm')}</span>
						</div>}
					</div>
				</div>
			</header>

			<main>
				<div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
					{/* Alert details */}
					{!!alertExecution.alertOutput.alert &&
						<div className={`${isInDrawer ? 'bg-gray-50' : 'bg-white'} rounded-xl mt-8 p-4 sm:p-8 relative`}>
							<div>
								<div className="flex flex-wrap items-center justify-between sm:flex-nowrap">
									<h3 className="text-base font-semibold leading-7 text-gray-900">Alert
										Configuration</h3>
									<Link
										to={`/secured/alert/${alertExecution.alertOutput.alert.type}/${alertExecution.alertOutput.alert.id}`}
										className="text-sm text-blue-600 font-semibold hover:text-blue-900">Edit</Link>
								</div>
								<div className="border-t border-gray-100 pt-5">
									<div
										className="text-sm leading-6 text-gray-600 grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
										<div className="flex flex-col gap-x-3">
											<span>Rate Comparison</span>
											<span className="text-xs text-gray-400 leading-none">Type</span>
										</div>
										<div className="flex flex-col gap-x-3">
                                            <span>
                                                {alertExecution.alertOutput.priceComparisonAlertRule.pricePoint === PriceComparisonAlertRulePricePoint.MEDIAN && 'Median'}
																							{alertExecution.alertOutput.priceComparisonAlertRule.pricePoint === PriceComparisonAlertRulePricePoint.MIN && 'Least Expensive'}
																							{alertExecution.alertOutput.priceComparisonAlertRule.pricePoint === PriceComparisonAlertRulePricePoint.MAX && 'Most Expensive'}
                                            </span>
											<span
												className="text-xs text-gray-400 leading-none">Price Calculation</span>
										</div>
										<div className="flex flex-col gap-x-3">
											<span>{alertExecution.alertOutput.priceComparisonAlertRule.priceRange!.from}% to {alertExecution.alertOutput.priceComparisonAlertRule.priceRange!.to}%</span>
											<span className="text-xs text-gray-400 leading-none">Range</span>
										</div>


										<div className="flex flex-col gap-x-3">
											<span>{alertExecution.alertOutput.alert.filter.onlyRefundable ? 'Lowest Flex' : 'Lowest'}</span>
											<span className="text-xs text-gray-400 leading-none">Rate</span>
										</div>
									</div>
								</div>

								<div className="mt-6">
									<p className="text-sm font-semibold text-gray-800 leading-none mb-2.5">Competitors</p>
									<div className="flex flex-wrap gap-x-3 gap-y-3">
										{alertExecution.alertOutput.competitors!.map(c => <Tag state="none"
																																					 extraClassName={`${isInDrawer ? 'bg-white' : 'bg-gray-50'}`}
																																					 key={c.id}>{c.name}</Tag>)}
									</div>
								</div>
							</div>
						</div>}
					{!areAllNew && !_isEmpty(updatedNotificationOutputs) && <div className="mt-6">
						<PriceComparisonAlertExecutionUpdatedNotificationsFragment alertExecution={alertExecution}
																																			 notificationOutputs={updatedNotificationOutputs}
																																			 marketStateDetailsModal={marketStateDetailsModal} />
					</div>}
					{!_isEmpty(notificationOutputs) && <div className="mt-6">
						<div className="sm:hidden">
							<label htmlFor="tabs" className="sr-only">
								Select a tab
							</label>
							{/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
							<select
								id="tabs"
								name="tabs"
								value={currentTabId}
								onChange={(e) => setCurrentTabId(e.target.value)}
								className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
							>
								{tabs.map((tab) => (
									<option key={tab.id} value={tab.id}>{tab.name}</option>
								))}
							</select>
						</div>
						<div className="hidden sm:block">
							<div className="border-b border-gray-200">
								<nav aria-label="Tabs" className="-mb-px flex">
									{tabs.map((tab) => (
										<a
											key={tab.name}
											onClick={() => setCurrentTabId(tab.id)}
											aria-current={currentTabId === tab.id ? 'page' : undefined}
											className={Utilities.classNames(
												currentTabId === tab.id
													? 'border-blue-500 text-blue-600'
													: 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
												'whitespace-nowrap border-b-2 px-6 py-4 text-sm font-medium cursor-pointer',
											)}
										>
											{tab.name}
										</a>
									))}
								</nav>
							</div>
						</div>
					</div>}
					{/* Content */}
					{!_isEmpty(notificationOutputs) && currentTabId === 'MOST_IMPORTANT' &&
						<PriceComparisonAlertExecutionMostImportantNotificationsViewFragment
							isInDrawer={isInDrawer}
							alertExecution={alertExecution}
							notificationOutputs={notificationOutputs}
							marketStateDetailsModal={marketStateDetailsModal} />}
					{!_isEmpty(notificationOutputs) && currentTabId === 'ALL' &&
						<PriceComparisonAlertExecutionAllNotificationsViewFragment alertExecution={alertExecution}
																																			 isInDrawer={isInDrawer}
																																			 notificationOutputs={notificationOutputs}
																																			 marketStateDetailsModal={marketStateDetailsModal} />}
					{(!alertExecution || _isEmpty(notificationOutputs)) &&
						<div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
							<Message state="warning">No results found</Message>
						</div>}
				</div>
			</main>
			<MarketStateDetailsModal companyHotel={companyHotel} ref={marketStateDetailsModal} settings={settings!} />
		</>;
	}
	return <></>;
};

export default PriceComparisonAlertExecutionFragment;